import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  createProfilePicture,
  updateProfilePicture,
  deleteProfilePicture,
} from '../../redux/user/profilePictures/userProfilePicturesThunks';
import { Camera, X, User, Loader2, Trash2 } from 'lucide-react';
import AvatarSelectorModal from './AvatarSelectorModal';
import { resolveMediaUrl } from '../../utils/resolveMediaUrl';

const AvatarUploader = ({ userId, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Redux state
  const { profilePictures, loading, error } = useSelector(
    (s) => s.profilePictures || { profilePictures: {}, loading: false, error: null }
  );
  const pictureUrl = profilePictures[userId] || null;

  // Local state
  const [preview, setPreview] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Reset preview when avatar changes
  useEffect(() => {
    if (!loading && pictureUrl) {
      setPreview(null);
      setHasError(false);
    }
  }, [pictureUrl, loading]);

  // Handle avatar selection from modal
  const handleAvatarSelect = async (selectedUrl) => {
    try {
      const action = pictureUrl ? updateProfilePicture : createProfilePicture;
      await dispatch(action({ userId, fileOrUrl: selectedUrl })).unwrap();
      setIsModalOpen(false);
      
      // Close parent modal if provided
      if (onClose) {
        setTimeout(() => onClose(), 300);
      }
    } catch (err) {
      console.error('Error setting avatar:', err);
    }
  };

  // Delete avatar with confirmation
  const handleDelete = async () => {
    try {
      await dispatch(deleteProfilePicture(userId)).unwrap();
      setPreview(null);
      setHasError(false);
      setShowDeleteConfirm(false);
    } catch (err) {
      console.error('Error deleting avatar:', err);
    }
  };

  const effectiveAvatar = preview || pictureUrl;

  return (
    <div className="space-y-6">
      {/* Avatar Display */}
      <div className="flex flex-col items-center">
        <div className="relative group">
          {/* Main Avatar */}
          <button
            onClick={() => setIsModalOpen(true)}
            disabled={loading}
            className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-2xl overflow-hidden border-4 border-cream-300 dark:border-gray-700/50 shadow-xl bg-cream-200 dark:bg-gray-800 flex items-center justify-center transition-all duration-300 hover:border-blue-500/50 hover:shadow-2xl hover:shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed group-hover:scale-105"
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center gap-3">
                <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
                <span className="text-xs text-cream-700 dark:text-gray-400">{t('profileWidgets.avatarUploader.updating')}</span>
              </div>
            ) : effectiveAvatar && !hasError ? (
              <img
                src={resolveMediaUrl(effectiveAvatar)}
                alt={t('profileWidgets.avatarUploader.profileAvatarAlt')}
                className="w-full h-full object-cover"
                onError={() => setHasError(true)}
              />
            ) : (
              <User className="w-16 h-16 sm:w-20 sm:h-20 text-cream-700 dark:text-gray-500" />
            )}

            {/* Overlay */}
            {!loading && (
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-2">
                <Camera className="w-8 h-8 text-white" />
                <span className="text-white text-sm font-medium">{t('profileWidgets.avatarUploader.changeAvatarOverlay')}</span>
              </div>
            )}
          </button>

          {/* Delete Button */}
          {pictureUrl && !loading && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-2 shadow-lg hover:bg-red-700 transition-all hover:scale-110 active:scale-95 z-10"
              aria-label={t('profileWidgets.avatarUploader.deleteAvatarAria')}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 text-center max-w-sm">
          <h3 className="text-cream-900 dark:text-white font-semibold mb-2">{t('profileWidgets.avatarUploader.profilePictureTitle')}</h3>
          <p className="text-sm text-cream-700 dark:text-gray-400 leading-relaxed">
            {t('profileWidgets.avatarUploader.instructions')}
          </p>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-600/50 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5">⚠️</div>
            <div>
              <h4 className="text-red-800 dark:text-red-200 font-medium mb-1">{t('profileWidgets.avatarUploader.errorTitle')}</h4>
              <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => setIsModalOpen(true)}
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-medium hover:from-blue-500 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-blue-500/25 active:scale-95"
        >
          <Camera className="w-5 h-5" />
          <span>{pictureUrl ? t('profileWidgets.avatarUploader.changeAvatarButton') : t('profileWidgets.avatarUploader.selectAvatarButton')}</span>
        </button>

        {onClose && (
          <button
            onClick={onClose}
            className="flex-1 sm:flex-none px-6 py-3 bg-cream-200 dark:bg-gray-700 text-cream-900 dark:text-gray-200 rounded-xl font-medium hover:bg-cream-300 dark:hover:bg-gray-600 transition-all active:scale-95"
          >
            {t('profileWidgets.avatarUploader.close')}
          </button>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 w-full max-w-md p-6 animate-slideUp">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-2.5 bg-red-600/20 rounded-xl">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-white text-lg font-semibold mb-2">{t('profileWidgets.avatarUploader.deleteConfirmTitle')}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {t('profileWidgets.avatarUploader.deleteConfirmBody')}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2.5 bg-gray-700 text-gray-200 rounded-xl font-medium hover:bg-gray-600 transition-all"
              >
                {t('profileWidgets.avatarUploader.cancel')}
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-all shadow-lg hover:shadow-red-500/25"
              >
                {t('profileWidgets.avatarUploader.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Avatar selection modal */}
      {isModalOpen && (
        <AvatarSelectorModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSelect={handleAvatarSelect}
          currentAvatar={pictureUrl}
          loading={loading}
        />
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AvatarUploader;